<div class="settings-page users-page">
  <div class="settings-topbar">
    <div class="topbar-left">
      <h1>
        User Permissions
        <small>System users and access roles</small>
      </h1>
      <div class="breadcrumb">
        <i class="bi bi-house-door"></i>
        Settings / <span class="current">User Permissions</span>
      </div>
    </div>
    <div class="topbar-right">
      <button type="button" class="btn btn-primary" (click)="openModal()">
        <i class="bi bi-plus-circle"></i> Add User
      </button>
    </div>
  </div>

  <div class="settings-table-wrap">
    <div class="table-scroll">
      <table class="report-data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Branch</th>
            <th>Permissions</th>
            <th>Status</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (row of users(); track row.id) {
            <tr>
              <td><span class="fw-semibold">{{ row.name }}</span></td>
              <td>{{ row.email }}</td>
              <td>
                <span class="meta-badge primary role-badge">
                  <i class="bi bi-shield-check"></i>
                  {{ getRoleName(row.role) }}
                </span>
              </td>
              <td>
                @if (row.branch) {
                  <span class="meta-badge accent branch-badge">
                    <i class="bi bi-building"></i>
                    {{ getBranchName(row.branch) }}
                  </span>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td>
                <span class="permission-count">
                  <i class="bi bi-lock"></i>
                  {{ getPermissionCount(getUserPermissions(row)) }} permissions
                </span>
              </td>
              <td>
                <span class="status-pill" [class.active]="row.status === 'active'">
                  {{ row.status }}
                </span>
              </td>
              <td class="actions-cell">
                <div class="row-actions">
                  <button type="button" class="btn-action edit" title="Edit user" (click)="openEdit(row)">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button type="button" class="btn-action delete" title="Delete user" (click)="deleteRow(row)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="7" class="text-center py-4 text-muted">
                <i class="bi bi-inbox empty-state-icon"></i>
                No users found. Click "Add User" to create one.
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</div>

@if (showModal()) {
  <div class="modal-overlay open" (click)="closeModal()">
    <div class="settings-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi" [class]="isEditing() ? 'bi-pencil' : 'bi-plus-circle'"></i>
          {{ isEditing() ? 'Edit User' : 'Add User' }}
        </h2>
        <button type="button" class="close-btn" (click)="closeModal()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="form-label">Full Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="userForm.name"
            placeholder="Enter full name"
            [class.is-invalid]="!userForm.name && userForm.name !== undefined"
          />
          @if (!userForm.name && userForm.name !== undefined) {
            <div class="invalid-feedback">Name is required</div>
          }
        </div>
        <div class="form-group">
          <label class="form-label">Email Address <span class="text-danger">*</span></label>
          <input
            type="email"
            class="form-control"
            [(ngModel)]="userForm.email"
            placeholder="Enter email address"
            [class.is-invalid]="!userForm.email && userForm.email !== undefined"
          />
          @if (!userForm.email && userForm.email !== undefined) {
            <div class="invalid-feedback">Email is required</div>
          }
        </div>
        <div class="form-group">
          <label class="form-label">
            Password
            @if (!isEditing()) {
              <span class="text-danger">*</span>
            }
          </label>
          <input
            type="password"
            class="form-control"
            [(ngModel)]="userForm.password"
            [placeholder]="isEditing() ? 'Leave blank to keep current password' : 'Enter a strong password'"
          />
          <small class="text-muted">
            Must include uppercase, lowercase, a number, and a special character.
          </small>
        </div>
        <div class="form-group">
          <label class="form-label">Role <span class="text-danger">*</span></label>
          <select
            class="form-select"
            [ngModel]="userForm.role"
            (ngModelChange)="onRoleChange($event)"
          >
            <option value="">Select a role</option>
            @for (role of availableRoles(); track role.id) {
              <option [value]="role.id">{{ role.name }}@if (role.description) { — {{ role.description }} }</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Branch Assignment <span class="text-danger">*</span></label>
          <select class="form-select" [(ngModel)]="userForm.branch">
            <option value="">Select branch</option>
            @for (branch of branches(); track branch.id) {
              <option [value]="branch.id">{{ branch.name }}</option>
            }
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Status</label>
          <select class="form-select" [(ngModel)]="userForm.status">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div class="form-group permissions-section">
          <div class="permissions-header">
            <label class="form-label mb-0">
              <i class="bi bi-shield-lock"></i> Role permissions
            </label>
            <button type="button" class="btn btn-outline-primary btn-sm" (click)="openPermissionsModal()">
              <i class="bi bi-gear"></i> Manage Permissions
            </button>
          </div>
          <small class="text-muted d-block mb-2">
            Permissions belong to the selected role and apply to every user with that role.
          </small>
          <div class="permissions-summary">
            @if (userForm.permissions && userForm.permissions.length > 0) {
              <div class="permission-tags">
                @for (perm of userForm.permissions; track perm) {
                  <span class="permission-tag">{{ getPermissionLabel(perm) }}</span>
                }
              </div>
            } @else {
              <small class="text-muted">
                <i class="bi bi-info-circle"></i> No permissions assigned
              </small>
            }
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="closeModal()">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="save()">
          <i class="bi bi-check-lg"></i> {{ isEditing() ? 'Save Changes' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
}

@if (showPermissionsModal()) {
  <div class="modal-overlay open" (click)="showPermissionsModal.set(false)">
    <div class="settings-modal settings-modal-wide permissions-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="bi bi-shield-lock"></i>
          Manage Permissions
        </h2>
        <button type="button" class="close-btn" (click)="showPermissionsModal.set(false)">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="permissions-grid">
          @for (permission of permissionCatalog(); track permission.id) {
            <div class="permission-item">
              <label class="permission-checkbox">
                <input
                  type="checkbox"
                  class="form-check-input"
                  [checked]="hasPermission(permission.id)"
                  (change)="togglePermission(permission.id)"
                />
                <span class="permission-label">
                  <i class="bi" [class]="permission.icon"></i>
                  {{ permission.label }}
                </span>
              </label>
              <small class="permission-desc">{{ permission.description }}</small>
            </div>
          }
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="showPermissionsModal.set(false)">
          <i class="bi bi-x-lg"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="savePermissions()">
          <i class="bi bi-check-lg"></i> Save Permissions
        </button>
      </div>
    </div>
  </div>
}

@if (showToast()) {
  <div class="toast-container show">
    <div class="toast" [class.success]="!toastIsError()" [class.error]="toastIsError()">
      <div class="toast-icon">
        <i class="bi" [class]="toastIsError() ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'"></i>
      </div>
      <div class="toast-content">
        <div class="toast-title">{{ toastIsError() ? 'Unable to save' : 'Success!' }}</div>
        <div class="toast-message">{{ toastMessage() }}</div>
      </div>
    </div>
  </div>
}
